import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { APP_PATH } from "../../api/backendPath";
import { toast } from "../../components/toast";

// Create axios instance with auth token
const createAxiosInstance = () => {
  const token = sessionStorage.getItem("usr_1a2b3c");
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

// Async thunk to fetch lab orders
export const fetchLabOrders = createAsyncThunk(
  "labOrders/fetchLabOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        limit = 10,
        search,
        testType,
        date,
        status,
        location,
        // Sort is optional. When the operator toggles an explicit column
        // sort (e.g. Request Date), the caller passes both fields so the
        // backend can return the globally-sorted page instead of us
        // reshuffling only what's already in memory.
        sortField,
        sortDirection,
      } = params;

      // Build query parameters
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search && search.trim()) {
        queryParams.append("search", search.trim());
      }
      if (testType && testType !== "All") {
        queryParams.append("testType", testType);
      }
      if (date) {
        queryParams.append("date", date);
      }
      if (status && status !== "All") {
        queryParams.append("status", status);
      }
      if (location) {
        queryParams.append("location", location);
      }
      // Only send sort params when an explicit column sort is active; when
      // the frontend is in the SLA-overdue-first "default" state, we leave
      // these off so the backend applies its existing default order.
      if (sortField && sortField !== "default") {
        queryParams.append("sortField", sortField);
        if (sortDirection) {
          queryParams.append("sortDirection", sortDirection);
        }
      }

      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${APP_PATH.getLabOrders}?${queryParams.toString()}`,
      );

      // Handle different response structures
      if (response.data?.data) {
        // API returns { status, data: { orders, pagination }, message }
        const responseData = response.data.data;
        return {
          data: responseData.orders || [],
          pagination: {
            currentPage: responseData.pagination?.page || 1,
            totalPages: responseData.pagination?.totalPages || 1,
            totalItems: responseData.pagination?.total || 0,
          },
        };
      } else if (response.data) {
        return response.data;
      } else {
        console.warn(
          "fetchLabOrders: Unexpected response structure, returning empty data",
        );
        return {
          data: [],
          pagination: { currentPage: 1, totalPages: 1, totalItems: 0 },
        };
      }
    } catch (error) {
      console.error("Fetch lab orders error:", error);
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch lab orders";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  },
);

const labOrdersSlice = createSlice({
  name: "labOrders",
  initialState: {
    data: [],
    isLoading: false,
    error: null,
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  },
  reducers: {
    clearLabOrders: (state) => {
      state.data = [];
      state.error = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLabOrders.fulfilled, (state, action) => {
        state.isLoading = false;

        // Handle null payload
        if (!action.payload) {
          console.warn(
            "fetchLabOrders: action.payload is null - setting empty state",
          );
          state.data = [];
          state.currentPage = 1;
          state.totalPages = 1;
          state.totalItems = 0;
          return;
        }

        state.data = action.payload.data || [];
        state.currentPage = action.payload.pagination?.currentPage || 1;
        state.totalPages = action.payload.pagination?.totalPages || 1;
        state.totalItems = action.payload.pagination?.totalItems || 0;
      })
      .addCase(fetchLabOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearLabOrders, setCurrentPage } = labOrdersSlice.actions;
export default labOrdersSlice.reducer;
